import { useMutation } from '@tanstack/react-query';

import { API } from '../../../shared/constants';
import { apiClient } from '../../../shared/lib/apiClient';
import { debug } from '../../../shared/lib/general';
import { isSubmittableAmount } from '../payment.utils';

interface SetOrderAmountParams {
    orderId: string;
    amount: number;
}

interface SetOrderAmountResponse {
    amount: number;
}

interface SetOrderAmountResult {
    /** Null when the submit was skipped — nothing was written. */
    data: SetOrderAmountResponse | null;
    requestedAmount: number;
    skipped: boolean;
}

/**
 * The single place the client writes order.amount, so the guard lives here:
 * `usePopcornAmount` calls this hook directly and would bypass one placed in
 * `useOrderAmount` (SWRN-1964).
 *
 * A skip resolves as an ordinary result rather than throwing — every amount
 * screen submits from an effect that also runs on mount, so rejecting would
 * report an error on each mount of each screen.
 */
export const useSetOrderAmount = () => {
    return useMutation({
        mutationFn: async ({
            orderId,
            amount,
        }: SetOrderAmountParams): Promise<SetOrderAmountResult> => {
            if (!isSubmittableAmount(amount)) {
                debug(`setOrderAmount skipped ${orderId} ${amount}`, 'data');
                return { data: null, requestedAmount: amount, skipped: true };
            }

            debug(`setOrderAmount ${orderId} ${amount}`, 'data');
            debug(`setOrderAmount URL: ${API.ORDER_AMOUNT}?id=${orderId}`, 'api');
            const { data } = await apiClient.post<SetOrderAmountResponse>(
                `${API.ORDER_AMOUNT}?id=${orderId}`,
                { amount },
            );
            debug('setOrderAmount data', 'state', data);
            return { data, requestedAmount: amount, skipped: false };
        },
    });
};
